import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { useFeatures } from '../services/features';
import { NUMBER_FORMAT_HINT, previewNumberFormat, validateNumberFormat } from '../services/invoiceNumbering';

// Walidacja PESEL — ta sama suma kontrolna, którą sprawdza backend
const validatePESEL = (value) => {
    const pesel = value.replace(/[-\s]/g, '');
    if (!/^\d{11}$/.test(pesel)) {
        return { valid: false, message: 'PESEL musi zawierać dokładnie 11 cyfr' };
    }
    const weights = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
    const sum = weights.reduce((acc, weight, i) => acc + weight * Number(pesel[i]), 0);
    if ((10 - (sum % 10)) % 10 !== Number(pesel[10])) {
        return { valid: false, message: 'Nieprawidłowa suma kontrolna PESEL' };
    }
    return { valid: true, message: '' };
};

// Komunikaty walidacji backendu przychodzą po angielsku
const translateUserError = (message) => {
    const translations = [
        [/Username, NIP or PESEL already registered/i, 'Ta nazwa użytkownika, NIP albo PESEL jest już zarejestrowany'],
        [/Exactly one of NIP and PESEL is required/i, 'Podaj NIP albo PESEL — dokładnie jeden z nich'],
        [/NIP must be 10 digits/i, 'NIP musi zawierać dokładnie 10 cyfr'],
        [/Invalid NIP checksum/i, 'Nieprawidłowa suma kontrolna NIP'],
        [/PESEL must be 11 digits/i, 'PESEL musi zawierać dokładnie 11 cyfr'],
        [/Invalid PESEL checksum/i, 'Nieprawidłowa suma kontrolna PESEL'],
    ];
    const match = translations.find(([pattern]) => pattern.test(message));
    return match ? match[1] : message;
};

const UserForm = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [taxId, setTaxId] = useState('');
    // Członek identyfikowany NIP-em (firma, osoba z działalnością) albo PESEL-em (osoba fizyczna bez NIP)
    const [identityType, setIdentityType] = useState('nip');
    const [pesel, setPesel] = useState('');
    const [peselError, setPeselError] = useState('');
    const [vatExempt, setVatExempt] = useState(false);
    const [address, setAddress] = useState('');
    const [bankAccountNumber, setBankAccountNumber] = useState('');
    const [parentUserId, setParentUserId] = useState('');
    const [invoiceNumberFormat, setInvoiceNumberFormat] = useState('');
    const [users, setUsers] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [nipError, setNipError] = useState('');
    const [editUserId, setEditUserId] = useState(null);
    const { username: editUsername } = useParams();
    const navigate = useNavigate();
    const features = useFeatures();
    const numberFormatError = invoiceNumberFormat.trim() ? validateNumberFormat(invoiceNumberFormat.trim()) : '';

    // Walidacja NIP - polska suma kontrolna
    const validateNIP = (nip) => {
        // Usuń myślniki i spacje
        const cleanNip = nip.replace(/[-\s]/g, '');

        if (!/^\d{10}$/.test(cleanNip)) {
            return { valid: false, message: 'NIP musi zawierać dokładnie 10 cyfr' };
        }

        const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
        let sum = 0;

        for (let i = 0; i < 9; i++) {
            sum += parseInt(cleanNip[i]) * weights[i];
        }

        const checksum = sum % 11;
        const lastDigit = parseInt(cleanNip[9]);

        if (checksum !== lastDigit) {
            return { valid: false, message: 'Nieprawidłowa suma kontrolna NIP' };
        }

        return { valid: true, message: '' };
    };

    const handleNipChange = (value) => {
        setTaxId(value);
        if (value.replace(/[-\s]/g, '').length === 10) {
            const validation = validateNIP(value);
            setNipError(validation.valid ? '' : validation.message);
        } else {
            setNipError('');
        }
    };

    const handlePeselChange = (value) => {
        setPesel(value);
        if (value.replace(/[-\s]/g, '').length === 11) {
            const validation = validatePESEL(value);
            setPeselError(validation.valid ? '' : validation.message);
        } else {
            setPeselError('');
        }
    };

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await api.get('/users/');
                const allUsers = response.data.filter(u => u.role === 'user');
                setUsers(allUsers);

                // Przy edycji — znajdź użytkownika na liście i wypełnij formularz
                if (editUsername) {
                    const userData = allUsers.find(u => u.username === editUsername);
                    if (userData) {
                        setEditUserId(userData.id);
                        setUsername(userData.username);
                        setFullName(userData.full_name || '');
                        setTaxId(userData.tax_id_number || '');
                        setPesel(userData.pesel || '');
                        setIdentityType(userData.pesel ? 'pesel' : 'nip');
                        setVatExempt(!!userData.vat_exempt);
                        setAddress(userData.address || '');
                        setBankAccountNumber(userData.bank_account_number || '');
                        setParentUserId(userData.parent_user_id ? String(userData.parent_user_id) : '');
                        setInvoiceNumberFormat(userData.invoice_number_format || '');
                    }
                }
            } catch (error) {
                console.error("Failed to fetch users", error);
            }
        };
        fetchUsers();
    }, [editUsername]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');

        // Walidacja wymaganych pól
        if (!fullName.trim()) {
            setErrorMessage('Nazwa pełna jest wymagana');
            return;
        }
        if (!address.trim()) {
            setErrorMessage('Adres jest wymagany');
            return;
        }

        const identityValidation = identityType === 'nip' ? validateNIP(taxId) : validatePESEL(pesel);
        if (!identityValidation.valid) {
            setErrorMessage(identityValidation.message);
            return;
        }

        if (identityType === 'nip' && numberFormatError) {
            setErrorMessage(numberFormatError);
            return;
        }

        // Backend przyjmuje dokładnie jedno z pól NIP / PESEL. vat_exempt jest obowiązkowe zawsze,
        // a PUT zastępuje cały rekord. Osoba z PESEL nie wystawia faktur: bez zwolnienia i bez formatu numeru.
        const identity = identityType === 'nip'
            ? {
                tax_id_number: taxId.replace(/[-\s]/g, ''),
                pesel: null,
                vat_exempt: vatExempt,
                invoice_number_format: invoiceNumberFormat.trim() || null,
            }
            : {
                tax_id_number: null,
                pesel: pesel.replace(/[-\s]/g, ''),
                vat_exempt: false,
                invoice_number_format: null,
            };

        try {
            if (editUsername) {
                // Aktualizacja użytkownika – nie edytujemy hasła ani roli
                await api.put(`/users/${editUserId}`, {
                    full_name: fullName,
                    ...identity,
                    address: address,
                    bank_account_number: bankAccountNumber || null,
                    parent_user_id: parentUserId || null,
                });
            } else {
                // Tworzenie nowego użytkownika – rola ustawiana jest domyślnie na "user"
                await api.post('/users/', {
                    username,
                    password,
                    full_name: fullName,
                    ...identity,
                    address,
                    bank_account_number: bankAccountNumber || null,
                    parent_user_id: parentUserId || null,
                    role: 'user',
                });
            }
            navigate('/admin');
        } catch (error) {
            console.error("Error saving user", error);
            const detail = error.response?.data?.detail;
            if (typeof detail === 'string') {
                setErrorMessage(translateUserError(detail));
            } else if (Array.isArray(detail)) {
                setErrorMessage(detail.map(e => translateUserError(e.msg.replace(/^Value error, /, ''))).join(', '));
            } else {
                setErrorMessage('Wystąpił błąd podczas zapisywania użytkownika');
            }
        }
    };

    // Filtruj użytkowników - nie można wybrać siebie jako rodzica
    const availableParentUsers = users.filter(u => u.username !== editUsername);

    return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
                <h2 className="text-2xl font-bold mb-6">
                    {editUsername ? 'Edytuj użytkownika' : 'Dodaj użytkownika'}
                </h2>

                {errorMessage && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                        {errorMessage}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2">Nazwa użytkownika:</label>
                        <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            disabled={!!editUsername}
                            required
                        />
                    </div>

                    {!editUsername && (
                        <div className="mb-4">
                            <label className="block text-gray-700 mb-2">Hasło:</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg"
                                required
                                minLength="8"
                                placeholder="Wprowadź hasło (min. 8 znaków)"
                            />
                        </div>
                    )}

                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2">
                            Pełna nazwa: <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            placeholder="Nazwa firmy lub imię i nazwisko"
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-gray-700">
                                {identityType === 'nip' ? 'NIP' : 'PESEL'}: <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-4 text-sm text-gray-700">
                                <label className="flex items-center gap-1">
                                    <input
                                        type="radio"
                                        name="identity-type"
                                        value="nip"
                                        checked={identityType === 'nip'}
                                        onChange={() => setIdentityType('nip')}
                                    />
                                    NIP
                                </label>
                                <label className="flex items-center gap-1">
                                    <input
                                        type="radio"
                                        name="identity-type"
                                        value="pesel"
                                        checked={identityType === 'pesel'}
                                        onChange={() => setIdentityType('pesel')}
                                    />
                                    PESEL
                                </label>
                            </div>
                        </div>
                        {identityType === 'nip' ? (
                            <>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={taxId}
                                    onChange={(e) => handleNipChange(e.target.value)}
                                    className={`w-full p-2 border rounded-lg ${nipError ? 'border-red-500' : 'border-gray-300'}`}
                                    placeholder="Wprowadź NIP (10 cyfr)"
                                    required
                                />
                                {nipError && (
                                    <p className="text-red-500 text-sm mt-1">{nipError}</p>
                                )}
                            </>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={pesel}
                                    onChange={(e) => handlePeselChange(e.target.value)}
                                    className={`w-full p-2 border rounded-lg ${peselError ? 'border-red-500' : 'border-gray-300'}`}
                                    placeholder="Wprowadź PESEL (11 cyfr)"
                                    required
                                />
                                {peselError && (
                                    <p className="text-red-500 text-sm mt-1">{peselError}</p>
                                )}
                                <p className="text-gray-500 text-xs mt-1">
                                    Osoba fizyczna bez NIP. Za sprzedaną energię spółdzielnia wystawia jej dowód zakupu; nie ma dostępu do KSeF.
                                </p>
                            </>
                        )}
                    </div>

                    {features?.invoicing && identityType === 'nip' && (
                        <div className="mb-4">
                            <label className="flex items-start gap-2 text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={vatExempt}
                                    onChange={(e) => setVatExempt(e.target.checked)}
                                    className="mt-1"
                                />
                                <span>
                                    Zwolniony z VAT (art. 113 ustawy o VAT)
                                    <span className="block text-xs text-gray-500">
                                        Sprzedaje energię bez VAT. Kupując od spółdzielni, nadal płaci VAT 23%.
                                    </span>
                                </span>
                            </label>
                        </div>
                    )}

                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2">
                            Adres: <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            placeholder="Wprowadź adres"
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2">Numer konta bankowego:</label>
                        <input
                            type="text"
                            value={bankAccountNumber}
                            onChange={(e) => setBankAccountNumber(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            placeholder="Wprowadź numer konta bankowego"
                        />
                    </div>

                    {features?.invoicing && identityType === 'nip' && (
                        <div className="mb-4">
                            <label className="block text-gray-700 mb-2">Format numeru faktury:</label>
                            <input
                                type="text"
                                value={invoiceNumberFormat}
                                onChange={(e) => setInvoiceNumberFormat(e.target.value)}
                                className={`w-full p-2 border rounded-lg font-mono ${numberFormatError ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="Puste — format spółdzielni, np. FV/{n}/{mm}/{yyyy}"
                            />
                            {numberFormatError ? (
                                <p className="text-red-500 text-sm mt-1">{numberFormatError}</p>
                            ) : invoiceNumberFormat.trim() && (
                                <p className="text-gray-600 text-sm mt-1">
                                    Przykład: <span className="font-mono">{previewNumberFormat(invoiceNumberFormat.trim())}</span>
                                </p>
                            )}
                            <p className="text-gray-500 text-xs mt-1">
                                {NUMBER_FORMAT_HINT}{' '}
                                Z miesiącem w formacie licznik zeruje się co miesiąc, bez — co rok.
                            </p>
                        </div>
                    )}

                    <div className="mb-6">
                        <label className="block text-gray-700 mb-2">Podmiot nadrzędny:</label>
                        <select
                            value={parentUserId}
                            onChange={(e) => setParentUserId(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                        >
                            <option value="">-- Brak --</option>
                            {availableParentUsers.map(user => (
                                <option key={user.id} value={String(user.id)}>
                                    {user.full_name || user.username}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={() => navigate('/admin')}
                            className="flex-1 bg-gray-300 text-gray-700 p-2 rounded-lg hover:bg-gray-400 transition"
                        >
                            Anuluj
                        </button>
                        <button
                            type="submit"
                            className="flex-1 bg-softTeal text-white p-2 rounded-lg hover:bg-warmOrange transition"
                        >
                            {editUsername ? 'Zaktualizuj użytkownika' : 'Utwórz użytkownika'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserForm;
